import { ensureSafeComponent } from '@embroider/util'
import Logger from 'customer-flows/utils/logger'
import { slots } from 'customer-flows/components/_config/slots'
import { type SlotEntry, type SlotType } from 'customer-flows/components/_config/registry'
import Step from 'customer-flows/flow/step'
import { routeMap } from 'customer-flows/router'
import { tracked } from '@glimmer/tracking'

/** Per-page overrides applied when the flow is built, keyed by page type. */
export type FlowOverrides = Partial<Record<PageType, { routeName?: string; settings?: ClientPageSettings }>>

export default class Page {
  static slots = import.meta.glob('../components/slots/**/*.gts')

  type: PageType
  flowIndex: number
  routeName: string
  steps: Step[] = []
  hero?: any
  footer?: any
  settings: Prettify<Omit<PageConfig, keyof BasePage>> = {}

  @tracked activeStep?: Step

  static async create(page: PageConfig, index: number, overrides?: FlowOverrides) {
    const { visible, slots, params, ...settings } = page

    if (visible === false) return

    const newPage = new Page()
    newPage.flowIndex = index
    newPage.type = page.__typename
    newPage.routeName = overrides?.[page.__typename]?.routeName ?? routeMap[page.__typename]
    newPage.settings = { ...settings, ...overrides?.[page.__typename]?.settings }

    await newPage._loadSlots(slots ?? [])
    await newPage._loadSteps(page.steps ?? [])

    // Skip alwaysVisible (decorative) steps when picking the initial active step.
    const firstRealStep = newPage.steps.find((step) => !step.alwaysVisible)

    if (firstRealStep) {
      newPage.setActiveStep(firstRealStep)
    }

    return newPage
  }

  setActiveStep(step: Step): void {
    if (step.id === this.activeStep?.id) return

    const groupedSteps = this.findGroupedSteps(step)
    const lastStepInGroup = groupedSteps.at(-1)

    this.activeStep = lastStepInGroup
  }

  findGroupedSteps(targetStep: Step): Step[] {
    const groupedSteps = [targetStep]
    const renderGroup = targetStep.renderGroup

    if (!renderGroup) return groupedSteps

    for (const step of this.steps) {
      if (step.renderGroup && step.renderGroup === renderGroup) {
        groupedSteps.push(step)
      }
    }

    return groupedSteps.sort((a, b) => (a.pageIndex ?? 0) - (b.pageIndex ?? 0))
  }

  async _loadSteps(steps: StepConfig[]) {
    const createdSteps = await Promise.all(steps
      .filter((stepConfig) => stepConfig.visible !== false)
      .map((stepConfig, index) => Step.create(this, stepConfig, index)))

    this.steps = createdSteps.filter((step) => !step.unsupported)
  }

  async _loadSlots(slots: string[]) {
    await Promise.all(slots.map(slotKey => {
      const slot = this._findSlot(slotKey)
      if (!slot) return

      this._loadSlot(slot)
    }))
  }

  async _loadSlot(slot: SlotEntry) {
    if (!(slot.type in this)) {
      throw new Error(`No slot type field found for ${slot.type}.`)
    }

    // return early if already loaded
    if (this[slot.type] !== undefined) {
      return
    }

    try {
      const slotModule = Page.slots[`../components/slots/${slot.type}/${slot.componentPath}.gts`]

      if (!slotModule) {
        throw new Error(`Component module not found for ${slot.type}:${slot.componentPath}.`)
      }

      const component = await slotModule() as { default: any }
      const imported = component?.default

      this[slot.type] = ensureSafeComponent(imported, this)
    } catch (e) {
      Logger.error(`Error importing component ${slot.type}/${slot.componentPath}`, {
        errorMessage: (e as Error).message
      })
      throw e
    }
  }

  _findSlot(slotKey: string): SlotEntry | undefined {
    const [type, nameVariant = ''] = slotKey.split(':') as [SlotType, string]
    const [name, variant] = nameVariant.split('@')

    if (!variant) {
      return
    }

    const slot = slots.find(entry => entry.type === type && entry.name === name && entry.variant === variant)

    if (!slot) {
      throw new Error(`No slot found for ${slotKey}.`)
    }

    if (slot.supportedPages && !slot.supportedPages.includes(this.type)) {
      throw new Error(`Slot '${slotKey}' is not supported on page '${this.type}'.`)
    }

    return slot
  }
}

declare global {
  export interface FlowPage extends Page { }
}
